import React, { useContext } from 'react';
import Type from 'prop-types';
import { noop } from 'lodash';
import { Divider, FormControlLabel, Switch } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { fieldTypes } from 'constants/form';
import DateRangePickerField from '../Form/DateRangePickerField';
import ChipGroup from '../Form/ChipGroup';
import ListPickerField from '../Form/ListPickerField';
import { RadioGroupFilter } from '../Form/RadioGroupFilter';
import { TextFieldFilter } from '../Form/TextFieldFilter';
import styles from './FilterSection.module.css';
import { FilterDialogContext } from '../Filters/FilterDialogContext';

const FilterSection = (props) => {
  const {
    name,
    label,
    options,
    type,
    showDivider,
    onChange,
    SearchViewProps,
    ...other
  } = props;
  const { setFieldValue, values } = useFormikContext();
  const isFilterDialog = useContext(FilterDialogContext);
  const renderListPicker = () => {
    return (
      <Field
        name={name}
        label={label}
        options={options}
        getOptionLabel={(option) => option?.name ?? ''}
        component={ListPickerField}
        ListPickerButtonProps={{ appearance: isFilterDialog ? 'filter' : 'default' }}
        onChange={onChange}
        SearchViewProps={{
          multiple: true,
          getOptionSelected: (option1, option2) => option1.id === option2.id,
          loading: false,
          ...SearchViewProps,
        }}
        {...other}
      />
    );
  };

  const renderChipGroup = () => {
    return (
      <Field
        name={name}
        label={label}
        options={options}
        component={ChipGroup}
        removable={isFilterDialog}
      />
    );
  };

  const renderTextField = () => {
    return <Field name={name} label={label} component={TextFieldFilter} />;
  };

  const renderSwitch = () => {
    return (
      <Field
        name={name}
        type="checkbox"
        component={Switch}
        color="primary"
        checked={values[name]}
        onChange={(event) => {
          onChange(event);
          setFieldValue(name, event.target.checked);
        }}
      />
    );
  };

  const renderSwitchWithLabel = () => {
    return <FormControlLabel control={renderSwitch()} label={label} />;
  };

  const renderDateRangePicker = () => {
    return (
      <Field
        name={name}
        component={DateRangePickerField}
        label={label}
        {...other}
      />
    );
  };

  const renderRadioGroup = () => {
    return (
      <Field
        name={name}
        label={label}
        options={options}
        component={RadioGroupFilter}
        onChange={onChange}
      />
    );
  };

  const renderField = () => {
    switch (type) {
      case fieldTypes.CHIP_GROUP:
        return renderChipGroup();
      case fieldTypes.AUTOCOMPLETE:
        return renderListPicker();
      case fieldTypes.SWITCH:
        return renderSwitchWithLabel();
      case fieldTypes.DATE_RANGE_PICKER:
        return renderDateRangePicker();
      case fieldTypes.RADIO_GROUP:
        return renderRadioGroup();
      case fieldTypes.TEXT_FIELD:
        return renderTextField();

      default:
        return null;
    }
  };

  return (
    <div data-filter-section={type}>
      {renderField()}

      {showDivider && <Divider classes={{ root: styles.sectionDivider }} />}
    </div>
  );
};

FilterSection.propTypes = {
  name: Type.string.isRequired,
  type: Type.string.isRequired,
  label: Type.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  options: Type.arrayOf(Type.object),
  showDivider: Type.bool,
  onChange: Type.func,
  getOptionLabel: Type.func,
  queryConfig: Type.object,
  SearchViewProps: Type.object,
  margin: Type.string,
  fullWidth: Type.bool,
};

FilterSection.defaultProps = {
  options: [],
  showDivider: true,
  onChange: noop,
};

export default FilterSection;
